/**
 * A company's data buyer and data seller roles, on its own page.
 *
 * "Buys from us" and "Sells to us" show the buyer or supplier record behind
 * the role: what routes to it, its last 30 days, its returns and billing
 * details, and its statements. Editing goes through the same endpoints and
 * permissions the old Buyers and Suppliers pages used (`edit_buyers`,
 * `edit_suppliers`, `manage_billing`), so nothing about who may change what
 * has moved. Routing, returns and statements keep reading the record by its
 * own id.
 */
import { useEffect, useState } from 'react';
import {
  Alert, Anchor, Badge, Button, Card, Group, Loader, Menu, SimpleGrid, Stack, Table, Text, Title,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconDots, IconPencil, IconPlayerPause, IconPlayerPlay, IconTrash } from '@tabler/icons-react';
import { Link } from 'react-router-dom';
import {
  useCompanyRoles, useRemoveCompanyRole, type CompanyDetail, type CompanyRoleKey, type RoleFigures,
} from '../../api/crm';
import { useEntities, useSaveEntity, type EntityKind } from '../../api/queries';
import { useStatements, formatPence as formatStatementPence, monthLabel } from '../../api/statements';
import { statementLink } from '../statements/StatementsPage';
import { formatGBP, formatNumber, FONTS, TOKENS } from '../../theme';
import { MonoLabel } from '../../ui';
import { useVocab } from '../../vocab';
import { BuyerReturnsFields, SupplierReturnsFields } from '../EntityBilling';
import { EntityRecordModal, type Entity } from '../EntityRecordModal';
import { SupplierCostFields, readSupplierCost, useCostWords, writeSupplierCost } from '../SupplierCost';
import { roleWord, useCrmPermissions } from './crmShared';

type Perms = ReturnType<typeof useCrmPermissions>;

function Figure({ label, value, hint }: { label: string; value: string; hint?: string }) {
  return (
    <div>
      <MonoLabel>{label}</MonoLabel>
      <div style={{ fontFamily: FONTS.mono, fontSize: 18, color: TOKENS.text, fontVariantNumeric: 'tabular-nums' }}>
        {value}
      </div>
      {hint && <Text size="xs" c="dimmed">{hint}</Text>}
    </div>
  );
}

const count = (n: number | null | undefined) => (n == null ? 'Not available' : formatNumber(n));
const pct = (r: number | null | undefined) => (r == null ? 'None sold' : `${(r * 100).toFixed(1)}%`);
const money = (n: number | null | undefined) => (n == null ? 'Not available' : formatGBP(n));

/** The record behind a role, as the routing pages list it, with its settings. */
function useRecord(kind: EntityKind, id: string | undefined): Entity | undefined {
  return useEntities(kind, !!id).data?.entities.find((e) => e.id === id);
}

/**
 * The record's header: its name and id, whether routing uses it, and what
 * can be done to it here: edit, switch off or on, take the role away.
 */
function RecordHeader({
  company, kind, role, record, active, removable, perms,
}: {
  company: CompanyDetail;
  kind: EntityKind;
  role: CompanyRoleKey;
  record: Entity | undefined;
  active: boolean;
  removable: boolean;
  perms: Perms;
}) {
  const v = useVocab();
  const canEdit = kind === 'buyers' ? perms.editBuyers : perms.editSuppliers;
  const save = useSaveEntity(kind);
  const remove = useRemoveCompanyRole();
  const [editing, setEditing] = useState(false);
  const noun = kind === 'buyers' ? 'buyer' : 'supplier';

  function setActive(on: boolean) {
    if (!record) return;
    save.mutate(
      { id: record.id, body: { name: record.name, email: record.email, phone: record.phone, active: on, settings: record.settings } },
      {
        onSuccess: () => notifications.show({
          color: 'teal',
          title: on ? 'Switched on' : 'Switched off',
          message: on
            ? `Routing can use ${record.name} again.`
            : `Routing stops using ${record.name}. Its history stays.`,
        }),
        onError: (e) => notifications.show({ color: 'red', title: 'Not changed', message: (e as Error).message }),
      },
    );
  }

  async function takeAway() {
    const word = roleWord(v, role).toLowerCase();
    if (!window.confirm(`Take the ${word} role away from ${company.name}? Its ${v.one(noun)} record is deleted.`)) return;
    try {
      await remove.mutateAsync({ id: company.id, role });
      notifications.show({ color: 'teal', title: 'Role removed', message: `${company.name} no longer holds the ${word} role.` });
    } catch (e) {
      notifications.show({ color: 'red', title: 'Not removed', message: (e as Error).message, autoClose: 10000 });
    }
  }

  return (
    <Group justify="space-between" align="flex-start" wrap="wrap">
      <Stack gap={2}>
        <Group gap={8}>
          <Text fw={600}>{record?.name ?? company.name}</Text>
          <Badge variant="light" color={active ? 'chartGreen' : 'gray'}>{active ? 'Active' : 'Off'}</Badge>
        </Group>
        <Text size="xs" c="dimmed" style={{ fontFamily: FONTS.mono }}>{`${v.One(noun)} record ${record?.id ?? ''}`}</Text>
      </Stack>
      {canEdit && record && (
        <Group gap="xs">
          <Button variant="default" size="xs" leftSection={<IconPencil size={14} />} onClick={() => setEditing(true)}>
            Edit record
          </Button>
          <Menu position="bottom-end" withinPortal>
            <Menu.Target>
              <Button variant="default" size="xs" px={8} aria-label={`More for the ${roleWord(v, role).toLowerCase()} role`}>
                <IconDots size={14} />
              </Button>
            </Menu.Target>
            <Menu.Dropdown>
              {active ? (
                <Menu.Item leftSection={<IconPlayerPause size={14} />} onClick={() => setActive(false)}>
                  Switch off (keep history)
                </Menu.Item>
              ) : (
                <Menu.Item leftSection={<IconPlayerPlay size={14} />} onClick={() => setActive(true)}>
                  Switch on
                </Menu.Item>
              )}
              <Menu.Item color="red" leftSection={<IconTrash size={14} />} onClick={takeAway} disabled={!removable}>
                {`Remove ${roleWord(v, role).toLowerCase()} role`}
              </Menu.Item>
              {!removable && (
                <Text size="xs" c="dimmed" px="sm" pb={6} maw={260}>
                  Still in use, so it can only be switched off.
                </Text>
              )}
            </Menu.Dropdown>
          </Menu>
        </Group>
      )}
      <EntityRecordModal kind={kind} entity={record ?? null} opened={editing} onClose={() => setEditing(false)} />
    </Group>
  );
}

/** Returns, billing and (for a supplier) cost: saved on the record's settings. */
function SettingsCard({ kind, record, canEdit }: { kind: EntityKind; record: Entity | undefined; canEdit: boolean }) {
  const v = useVocab();
  const save = useSaveEntity(kind);
  const [settings, setSettings] = useState<unknown>(record?.settings ?? {});
  useEffect(() => setSettings(record?.settings ?? {}), [record]);
  const dirty = JSON.stringify(settings) !== JSON.stringify(record?.settings ?? {});
  const supplier = kind === 'suppliers';

  function submit() {
    if (!record) return;
    save.mutate(
      { id: record.id, body: { name: record.name, email: record.email, phone: record.phone, active: record.active, settings } },
      {
        onSuccess: () => notifications.show({ color: 'teal', title: 'Saved', message: record.name }),
        onError: (e) => notifications.show({ color: 'red', title: 'Not saved', message: (e as Error).message }),
      },
    );
  }

  return (
    <Card p="md">
      <Stack gap="sm">
        <Title order={4}>{supplier ? 'Cost, chargebacks and payment' : 'Returns and billing'}</Title>
        {!record ? (
          <Loader size="sm" />
        ) : (
          <>
            {supplier && (
              <SupplierCostFields
                value={readSupplierCost(settings)}
                disabled={!canEdit}
                onChange={(cost) => setSettings(writeSupplierCost(settings, cost))}
              />
            )}
            {supplier
              ? <SupplierReturnsFields settings={settings} disabled={!canEdit} onChange={setSettings} />
              : <BuyerReturnsFields settings={settings} disabled={!canEdit} onChange={setSettings} />}
            {canEdit ? (
              <Group justify="flex-end">
                <Button onClick={submit} disabled={!dirty} loading={save.isPending}>Save</Button>
              </Group>
            ) : (
              <Text size="xs" c="dimmed">
                {`Changing these needs permission to edit ${v.many(supplier ? 'supplier' : 'buyer')}.`}
              </Text>
            )}
          </>
        )}
      </Stack>
    </Card>
  );
}

/** Issued statements for the record (manage_billing). */
function StatementsCard({ kind, partyId, perms }: { kind: 'buyer' | 'supplier'; partyId: string; perms: Perms }) {
  const list = useStatements({ kind, party_id: partyId }, perms.manageBilling);
  if (!perms.manageBilling) return null;
  const rows = list.data?.statements ?? [];
  return (
    <Card p={0}>
      <Group justify="space-between" p="md" pb="xs">
        <Title order={4}>Statements</Title>
        <Anchor component={Link} to={`/statements?kind=${kind}`} size="sm">All statements</Anchor>
      </Group>
      {list.isPending ? (
        <Group justify="center" p="md"><Loader size="sm" /></Group>
      ) : rows.length === 0 ? (
        <Text size="sm" c="dimmed" px="md" pb="md">No statements issued yet. Drafts are on the Statements page.</Text>
      ) : (
        <Table.ScrollContainer minWidth={420}>
          <Table verticalSpacing={6}>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Number</Table.Th>
                <Table.Th>Month</Table.Th>
                <Table.Th ta="right">Total</Table.Th>
                <Table.Th>Status</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {rows.map((s) => (
                <Table.Tr key={s.id ?? `${s.period}-${s.party_id}`}>
                  <Table.Td><Anchor component={Link} to={statementLink(s)} size="sm">{s.number ?? 'Draft'}</Anchor></Table.Td>
                  <Table.Td><Text size="sm">{monthLabel(s.period)}</Text></Table.Td>
                  <Table.Td ta="right"><span style={{ fontFamily: FONTS.mono, fontSize: 13 }}>{formatStatementPence(s.totals.total_pence)}</span></Table.Td>
                  <Table.Td>
                    <Badge variant="light" color={s.status === 'paid' ? 'teal' : s.status === 'finalised' ? 'blue' : 'gray'}>
                      {s.status === 'paid' ? 'Paid' : s.status === 'finalised' ? 'Issued' : 'Draft'}
                    </Badge>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </Table.ScrollContainer>
      )}
    </Card>
  );
}

function CampaignList({ campaigns, empty }: { campaigns: { id: string; name: string; rows_30d?: number | null }[]; empty: string }) {
  if (campaigns.length === 0) return <Text size="sm" c="dimmed">{empty}</Text>;
  return (
    <Group gap="xs" wrap="wrap">
      {campaigns.map((c) => (
        <Badge
          key={c.id}
          component={Link}
          to={`/campaigns/${c.id}`}
          variant="light"
          color="gray"
          radius="sm"
          tt="none"
          style={{ cursor: 'pointer' }}
        >
          {c.rows_30d != null ? `${c.name} · ${formatNumber(c.rows_30d)}` : c.name}
        </Badge>
      ))}
    </Group>
  );
}

/** "Buys from us": the company as a data buyer. */
export function BuysFromUs({ company, perms }: { company: CompanyDetail; perms: Perms }) {
  const v = useVocab();
  const roles = useCompanyRoles(company.id);
  const b = roles.data?.buyer;
  const record = useRecord('buyers', b?.id ?? company.partner?.id);
  if (roles.error) return <Alert color="red" variant="light">{roles.error.message}</Alert>;
  if (!b) return <Group justify="center" p="xl"><Loader /></Group>;
  const f: RoleFigures = b.last_30_days;
  return (
    <Stack gap="md" data-testid="buys-from-us">
      <Card p="md">
        <Stack gap="md">
          <RecordHeader
            company={company} kind="buyers" role="data_buyer" record={record}
            active={b.active} removable={b.usage.removable} perms={perms}
          />
          <SimpleGrid cols={{ base: 2, sm: 4 }}>
            <Figure label={`Bought, 30 days`} value={count(f.rows)} />
            <Figure label="Paid, 30 days" value={money(f.revenue)} hint="Returns taken off" />
            <Figure label="Returned, 30 days" value={formatNumber(f.returned)} hint={f.rows ? pct(f.return_rate) : undefined} />
            <Figure
              label="Return window"
              value={b.return_window_days === 0 ? 'No returns' : `${b.return_window_days} days`}
              hint={b.own_return_window ? 'Its own' : "The organisation's default"}
            />
          </SimpleGrid>
          <div>
            <MonoLabel style={{ marginBottom: 6 }}>{`${v.Many('campaign')} it buys from`}</MonoLabel>
            <CampaignList campaigns={b.campaigns} empty={`No ${v.one('campaign')} sends to it yet.`} />
          </div>
        </Stack>
      </Card>

      <Card p={0}>
        <Group justify="space-between" p="md" pb="xs">
          <Title order={4}>{`Routing: ${v.many('destination')} and accepted when`}</Title>
          <Anchor component={Link} to="/destinations" size="sm">{`All ${v.many('destination')}`}</Anchor>
        </Group>
        {b.destinations.length === 0 ? (
          <Text size="sm" c="dimmed" px="md" pb="md">{`Nothing routes to it. Add ${v.a('destination')} for it to buy.`}</Text>
        ) : (
          <Table.ScrollContainer minWidth={640}>
            <Table verticalSpacing={8} data-testid="buyer-destinations">
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>{v.One('destination')}</Table.Th>
                  <Table.Th>{`${v.One('campaign')} and ${v.one('routeGroup')}`}</Table.Th>
                  <Table.Th ta="right">Price</Table.Th>
                  <Table.Th>Accepted when</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {b.destinations.map((d) => (
                  <Table.Tr key={d.id}>
                    <Table.Td>
                      <Anchor component={Link} to={`/destinations/${d.id}`} size="sm" fw={500}>{d.name || d.id}</Anchor>
                      <Group gap={4} mt={2}>
                        <Badge size="xs" variant="light" color={d.active ? 'chartGreen' : 'gray'}>{d.active ? 'On' : 'Off'}</Badge>
                        {d.delivery_method && <Text size="xs" c="dimmed">{d.delivery_method.replace('_', ' ')}</Text>}
                      </Group>
                    </Table.Td>
                    <Table.Td>
                      {d.links.length === 0 ? (
                        <Text size="sm" c="dimmed">{`Not in any ${v.one('routeGroup')}`}</Text>
                      ) : d.links.map((l) => (
                        <Text key={l.group_id} size="sm" c={l.active ? undefined : 'dimmed'}>
                          {`${l.campaign_name} › ${l.group_name}${l.active ? '' : ' (off)'}`}
                        </Text>
                      ))}
                    </Table.Td>
                    <Table.Td ta="right">
                      {d.links.map((l) => (
                        <div key={l.group_id} style={{ fontFamily: FONTS.mono, fontSize: 13 }}>{formatGBP(l.price)}</div>
                      ))}
                    </Table.Td>
                    <Table.Td><Text size="sm">{d.accepted_when}</Text></Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </Table.ScrollContainer>
        )}
      </Card>

      <SimpleGrid cols={{ base: 1, md: 2 }} spacing="md">
        <SettingsCard kind="buyers" record={record} canEdit={perms.editBuyers} />
        <StatementsCard kind="buyer" partyId={b.id} perms={perms} />
      </SimpleGrid>
    </Stack>
  );
}

/** "Sells to us": the company as a data seller. */
export function SellsToUs({ company, perms }: { company: CompanyDetail; perms: Perms }) {
  const v = useVocab();
  const costWords = useCostWords();
  const roles = useCompanyRoles(company.id);
  const s = roles.data?.supplier;
  const record = useRecord('suppliers', s?.id ?? company.source?.id);
  if (roles.error) return <Alert color="red" variant="light">{roles.error.message}</Alert>;
  if (!s) return <Group justify="center" p="xl"><Loader /></Group>;
  const f: RoleFigures = s.last_30_days;
  const cost = record ? costWords(readSupplierCost(record.settings)) : null;
  return (
    <Stack gap="md" data-testid="sells-to-us">
      <Card p="md">
        <Stack gap="md">
          <RecordHeader
            company={company} kind="suppliers" role="data_seller" record={record}
            active={s.active} removable={s.usage.removable} perms={perms}
          />
          <SimpleGrid cols={{ base: 2, sm: 4 }}>
            <Figure label={`${v.Many('lead')} sent, 30 days`} value={count(f.rows)} />
            <Figure label={`${v.p.Sold}, 30 days`} value={count(f.sold)} />
            <Figure label="Return rate" value={pct(f.return_rate)} hint={`${formatNumber(f.returned)} charged back in 30 days`} />
            <Figure label="Cost, 30 days" value={money(f.cost)} hint={cost ?? 'No cost set'} />
          </SimpleGrid>
          <div>
            <MonoLabel style={{ marginBottom: 6 }}>{`${v.Many('campaign')} it sends into, 30 days`}</MonoLabel>
            <CampaignList campaigns={s.campaigns} empty={`No ${v.many('lead')} from it in the last 30 days.`} />
          </div>
          {s.campaign_costs.length > 0 && (
            <div>
              <MonoLabel style={{ marginBottom: 6 }}>{`Cost on each ${v.one('campaign')}`}</MonoLabel>
              <Stack gap={2}>
                {s.campaign_costs.map((c) => (
                  <Text key={c.campaign_id} size="sm">
                    <Anchor component={Link} to={`/campaigns/${c.campaign_id}`}>{c.campaign_name}</Anchor>
                    {`: ${formatGBP(c.amount)} per ${v.one('lead')}`}
                  </Text>
                ))}
              </Stack>
            </div>
          )}
        </Stack>
      </Card>
      <SimpleGrid cols={{ base: 1, md: 2 }} spacing="md">
        <SettingsCard kind="suppliers" record={record} canEdit={perms.editSuppliers} />
        <StatementsCard kind="supplier" partyId={s.id} perms={perms} />
      </SimpleGrid>
    </Stack>
  );
}

/**
 * The overview's role summary: one line per role with its headline figures
 * and a link to its tab. Only roles this person may see.
 */
export function RoleSummary({
  company, perms, onOpen,
}: { company: CompanyDetail; perms: Perms; onOpen: (tab: 'buys' | 'sells') => void }) {
  const v = useVocab();
  const showBuyer = !!company.partner && perms.seesBuyers;
  const showSeller = !!company.source && perms.seesSuppliers;
  const roles = useCompanyRoles(company.id, showBuyer || showSeller);
  if (!showBuyer && !showSeller) return null;
  const b = roles.data?.buyer;
  const s = roles.data?.supplier;
  return (
    <Card p="md" data-testid="role-summary">
      <Stack gap="sm">
        <MonoLabel>Roles</MonoLabel>
        {roles.isPending && <Loader size="sm" />}
        {showBuyer && b && (
          <Group justify="space-between" wrap="nowrap" align="flex-start">
            <div>
              <Group gap={6}>
                <Text fw={600} size="sm">Buys from us</Text>
                <Badge size="xs" variant="light" color={b.active ? 'blue' : 'gray'}>{b.active ? v.p.dataBuyer : `${v.p.dataBuyer}, off`}</Badge>
              </Group>
              <Text size="sm" c="dimmed">
                {[
                  `${count(b.last_30_days.rows)} bought in 30 days`,
                  `${b.destinations.length} ${b.destinations.length === 1 ? v.one('destination') : v.many('destination')}`,
                  b.return_window_days === 0 ? 'no returns' : `${b.return_window_days} day return window`,
                ].join(' · ')}
              </Text>
            </div>
            <Anchor component="button" size="sm" onClick={() => onOpen('buys')}>Open</Anchor>
          </Group>
        )}
        {showSeller && s && (
          <Group justify="space-between" wrap="nowrap" align="flex-start">
            <div>
              <Group gap={6}>
                <Text fw={600} size="sm">Sells to us</Text>
                <Badge size="xs" variant="light" color={s.active ? 'grape' : 'gray'}>{s.active ? v.p.dataSeller : `${v.p.dataSeller}, off`}</Badge>
              </Group>
              <Text size="sm" c="dimmed">
                {[
                  `${count(s.last_30_days.rows)} ${v.many('lead')} in 30 days`,
                  `${pct(s.last_30_days.return_rate)} returned`,
                ].join(' · ')}
              </Text>
            </div>
            <Anchor component="button" size="sm" onClick={() => onOpen('sells')}>Open</Anchor>
          </Group>
        )}
      </Stack>
    </Card>
  );
}
